// apps/web/src/components/reports/campaign-performance/CampaignPerformanceTable.tsx
/**
 * Campaign Performance — the results table.
 *
 * Same header chrome, row height, sticky-column shadows and colour tokens as
 * TeamPerformanceTable, so every report in this module reads as one product.
 */
import { type JSX } from 'react'

import {
  CAMPAIGN_PERFORMANCE_COLUMNS,
  type ReportColumnDef,
} from './columns.js'
import type { CampaignPerformanceRow } from '../../../hooks/useCampaignPerformanceReport.js'
import { formatDuration } from '../../../utils/formatDate.js'

interface CampaignPerformanceTableProps {
  rows: CampaignPerformanceRow[]
  visibleColumns: string[]
}

function Blank(): JSX.Element {
  return <span className="text-slate-300">—</span>
}

function cellFor(
  row: CampaignPerformanceRow,
  key: string,
  showCampaign = true,
): JSX.Element {
  if (key === 'campaignName') {
    if (!showCampaign) return <Blank />
    return (
      <span className="truncate font-semibold text-slate-900" title={row.campaignName}>
        {row.campaignName}
      </span>
    )
  }
  const value: unknown = (row as unknown as Record<string, unknown>)[key]
  if (key === 'avgWaitTime' || key === 'avgResponseTime' || key === 'avgResolutionTime') {
    const sec = Number(value ?? 0)
    if (!Number.isFinite(sec) || sec < 0) return <Blank />
    return <span className="tabular-nums text-slate-700">{formatDuration(sec * 1000)}</span>
  }
  if (key === 'responseSlaPct' || key === 'resolutionSlaPct') {
    const pct = Number(value ?? 0)
    if (!Number.isFinite(pct)) return <Blank />
    return <span className="tabular-nums text-slate-700">{pct.toFixed(1)}%</span>
  }
  if (typeof value !== 'number') return <Blank />
  return (
    <span className={`tabular-nums ${value === 0 ? 'text-slate-300' : 'text-slate-700'}`}>
      {value}
    </span>
  )
}

export default function CampaignPerformanceTable({
  rows,
  visibleColumns,
}: CampaignPerformanceTableProps): JSX.Element {
  const byKey = new Map(CAMPAIGN_PERFORMANCE_COLUMNS.map((c) => [c.key, c]))

  const ordered: ReportColumnDef[] = [
    ...CAMPAIGN_PERFORMANCE_COLUMNS.filter((c) => c.locked && visibleColumns.includes(c.key)),
    ...visibleColumns
      .map((k) => byKey.get(k))
      .filter((c): c is ReportColumnDef => c !== undefined && !c.locked),
  ]

  let stickyAcc = 0
  const laidOut = ordered.map((c) => {
    if (c.stickyLeft === undefined) return c
    const stickyLeft = stickyAcc
    stickyAcc += c.width
    return { ...c, stickyLeft }
  })
  const lastStickyKey = [...laidOut].reverse().find((c) => c.stickyLeft !== undefined)?.key

  return (
    <table
      className="w-full min-w-max border-separate border-spacing-0 text-[13px]"
      style={{ tableLayout: 'fixed' }}
    >
      <thead>
        <tr>
          {laidOut.map((col) => (
            <th
              key={col.key}
              style={{
                width: col.width,
                minWidth: col.width,
                ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
              }}
              className={`relative sticky top-0 h-11 border-b border-slate-200 bg-[#FAFBFC] px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500 ${
                col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
              } ${col.stickyLeft !== undefined ? 'z-30' : 'z-20'} ${
                col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(229,231,235)]' : ''
              }`}
            >
              <span
                className={`inline-flex items-center gap-1.5 ${
                  col.align === 'right' ? 'flex-row-reverse' : ''
                }`}
              >
                {col.icon && <col.icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                <span className="truncate">{col.label}</span>
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const cellBg = 'bg-white group-hover:bg-[#F7F9FC]'
          const showCampaign =
            i === 0 || row.campaignName !== rows[i - 1]?.campaignName
          return (
            <tr key={`${row.campaignId}:${i}`} className="group">
              {laidOut.map((col) => (
                <td
                  key={col.key}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
                  }}
                  className={`h-[54px] border-b border-slate-100 px-4 align-middle text-slate-700 transition-colors ${cellBg} ${
                    col.stickyLeft !== undefined ? 'sticky z-10' : ''
                  } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${
                    col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(243,244,246)]' : ''
                  }`}
                >
                  <div
                    className={
                      col.align === 'center'
                        ? 'flex items-center justify-center'
                        : col.align === 'right'
                          ? 'flex items-center justify-end'
                          : 'truncate'
                    }
                  >
                    {cellFor(row, col.key, showCampaign)}
                  </div>
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
